import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, EASE } from "./Reveal";
import { FABRIC_CATEGORIES } from "@/lib/svenData";

/*
 * FABRIC LIBRARY — swatch imagery is TEMPORARY PLACEHOLDER (client's real
 * swatch photography zip pending). Swap image paths in svenData.js when it arrives.
 */
export const FabricLibrary = () => {
  const [catId, setCatId] = useState(FABRIC_CATEGORIES[0].id);
  const category = FABRIC_CATEGORIES.find((c) => c.id === catId);
  const [selected, setSelected] = useState(category.fabrics[0]);

  const pickCategory = (c) => {
    setCatId(c.id);
    setSelected(c.fabrics[0]);
  };

  return (
    <section id="fabrics" data-testid="fabric-library-section" className="bg-[#ffffff] py-20 lg:py-10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline-label mb-5">The Fabric Library</p>
          <h2 className="font-display text-3xl sm:text-3xl lg:text-4xl font-medium text-[#000000] mb-14">
            Begin with the <em className="font-accent text-3xl sm:text-3xl lg:text-4xl text-[#99732b]">Cloth.</em>

            <Reveal delay={0.2}>
            <p className="mt-6 text-sm sm:text-base font-light leading-relaxed text-[#141414] max-w-xl">
            Browse a curated selection online, shortlist your favourites and bring your fabric choices into your consultation. Your selected references can be saved to your appointment.
            </p>
          </Reveal>
          </h2>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={selected.id}
                    data-testid="fabric-preview-image"
                    src={selected.img}
                    alt={selected.name}
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6, ease: EASE }}
                    className="top-4 left-4 w-full aspect-square object-cover"
                  />
                </AnimatePresence>
                <div className="absolute top-0 left-0 bg-[#000000]/80 backdrop-blur-md px-4 py-2">
                  <p className="font-mono-ov text-[12px] tracking-[0.2em] uppercase text-[#ffffff]">{category.label}</p>
                </div>
              </div>
              <div data-testid="fabric-details" className="mt-6 grid grid-cols-2 gap-x-8 gap-y-4 border-t border-[#e7e2da] pt-6">
                <div className="col-span-2">
                  <h3 className="font-display text-2xl text-[#141414]">{selected.name}</h3>
                </div>
                {[
                  ["Composition", selected.composition],
                  ["Weight", selected.weight],
                  ["Season", selected.season],
                  ["Best For", selected.bestFor],
                ].map(([k, v]) => (
                  <div key={k}>
                    <p className="font-mono-ov text-[9px] tracking-[0.2em] uppercase text-[#c5a059] mb-1">{k}</p>
                    <p className="text-sm font-light text-[#141414]">{v}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-2 mb-10">
                {FABRIC_CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`fabric-category-${c.id}`}
                    onClick={() => pickCategory(c)}
                    className={`px-5 py-2 rounded-full font-mono-ov text-[10px] tracking-[0.15em] uppercase border transition-all duration-300 ${
                      catId === c.id
                        ? "bg-[#0b0b0c] text-[#ffffff] border-[#0b0b0c]"
                        : "text-[#000000] border-[#000000] hover:border-[#c5a059]"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
                {category.fabrics.map((f) => (
                  <motion.button
                    key={f.id}
                    data-testid={`fabric-swatch-${f.id}`}
                    onClick={() => setSelected(f)}
                    whileHover={{ y: -4 }}
                    whileTap={{ scale: 0.96 }}
                    className={`group text-left transition-all duration-300 ${selected.id === f.id ? "" : "opacity-90 hover:opacity-100"}`}
                  >
                    <div className={`overflow-hidden rounded-sm ring-offset-2 ring-offset-[#faf8f5] transition-all duration-300 ${selected.id === f.id ? "ring-2 ring-[#c5a059]" : "ring-1 ring-[#e7e2da]"}`}>
                      <img src={f.img} alt={f.name} loading="lazy" className="aspect-square w-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    </div>
                    <p className={`mt-2.5 font-mono-ov text-[10px] tracking-[0.12em] uppercase leading-relaxed ${selected.id === f.id ? "text-[#141414]" : "text-[#57534e]"}`}>{f.name}</p>
                  </motion.button>
                ))}
              </div>

              <p className="mt-10 text-s font-light text-[#57534e]/70 italic">
                A selection from the atelier library — visit us in Santacruz or Bandra to feel the full range in hand.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
